import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getGroupUsers } from 'core/crud/groups.crud';
import { useGroups } from 'core/hooks/useGroups';
import * as coreEntities from 'core/store/index';
import { parseUsers } from 'core/store/parsers';
import { filterStudents } from 'core/utils/user';

export type StudentInfo = {
  avatar: string | null;
  groupName: string;
  lastName: string;
  name: string;
};

type UseStudentsMapReturn = {
  loading: boolean;
  studentsMap: Map<string, StudentInfo>;
};

function useStudentsMap(): UseStudentsMapReturn {
  const { groups } = useGroups();
  const groupsLoading = useSelector((state: unknown) => coreEntities.groups.selectors.getGroupsLoading(state));
  const user = useSelector((state: unknown) => coreEntities.auth.selectors.getUser(state));

  const [studentsMap, setStudentsMap] = useState<Map<string, StudentInfo>>(new Map());
  const [fetching, setFetching] = useState<boolean>(false);

  const schoolGuid = (user as any)?.schools?.[0]?.guid || null;

  type GroupEntry = { guid: string; name: string };

  const groupEntries = useMemo((): GroupEntry[] => {
    const schoolGroups = groups?.schoolGroups || [];
    return schoolGroups.map((g: GroupEntry) => ({
      guid: g.guid,
      name: g.name,
    }));
  }, [groups]);

  const fetchStudents = useCallback(async () => {
    if (!schoolGuid || groupEntries.length === 0) return;

    setFetching(true);
    const map = new Map<string, StudentInfo>();

    try {
      const promises = groupEntries.map((entry) => getGroupUsers({ schoolGuid, groupGuid: entry.guid }));
      const responses = await Promise.all(promises);

      responses.forEach((response: any, index: number) => {
        const groupName = groupEntries[index].name;
        const users = parseUsers(response?.data?.data?.users || []);
        const students = filterStudents(users);

        students.forEach((student: any) => {
          if (student.guid && !map.has(student.guid)) {
            map.set(student.guid, {
              avatar: student.avatar || null,
              groupName,
              lastName: student.lastName || student.lastname || '',
              name: student.name || '',
            });
          }
        });
      });
    } catch (err) {
      console.error('[useStudentsMap] Error fetching group users:', err);
    }

    setStudentsMap(map);
    setFetching(false);
  }, [schoolGuid, groupEntries]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { loading: !!groupsLoading || fetching, studentsMap };
}

export default useStudentsMap;
